/**
 * GDJ quick-add + cart-state — adds products to the cart from .gdj-card hover
 * buttons, fully integrated with Horizon's cart system, and keeps an "in cart"
 * indicator on every card.
 *
 *  - Single-variant products add instantly; multi-variant products open the PDP.
 *  - If the product is already in the cart, a popup asks how many MORE to add.
 *  - Each card shows a subtle "N x [cart]" badge while it's in the cart.
 *
 * It dispatches Horizon's own CartLinesUpdateEvent and resolves it with the
 * refreshed cart + re-rendered sections, so the cart icon, drawer and toast all
 * behave exactly like a normal add-to-cart.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/**
 * Quantity currently in cart, keyed both by product_id AND by handle. Handle is
 * the reliable key: card ids can come from a stale Algolia index whose product
 * ids no longer match Shopify's, whereas handles stay stable — so badges match
 * by handle first and fall back to product_id.
 */
let cartMap = {};
let cartByHandle = {};

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
}

function cartQty(productId, handle) {
  if (handle && cartByHandle[String(handle)] != null) return cartByHandle[String(handle)];
  return cartMap[String(productId)] || 0;
}

function updateBadges() {
  document.querySelectorAll('.gdj-card').forEach((card) => {
    const badge = card.querySelector('[data-gdj-incart]');
    if (!badge) return;
    const qty = cartQty(card.dataset.productId, card.dataset.productHandle);
    const count = badge.querySelector('[data-gdj-incart-qty]');
    if (qty > 0) {
      if (count) count.textContent = qty;
      badge.hidden = false;
    } else {
      badge.hidden = true;
    }
  });
}

function applyCart(cart) {
  cartMap = {};
  cartByHandle = {};
  (cart.items || []).forEach((it) => {
    const pk = String(it.product_id);
    cartMap[pk] = (cartMap[pk] || 0) + it.quantity;
    if (it.handle) cartByHandle[String(it.handle)] = (cartByHandle[String(it.handle)] || 0) + it.quantity;
  });
  updateBadges();
}

async function refreshCartState() {
  try {
    const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    applyCart(cart);
  } catch (_) {
    updateBadges();
  }
}

function getProduct(handle) {
  return fetch(`${root()}products/${handle}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
}

/**
 * Search-to-cart conversion signal, mirrors trackView's Loi 25 gate in
 * gdj-search-collection.js: only fires after explicit consent. Uses the same
 * 'add_to_cart' event type the engine already whitelists (src/routes/events.js)
 * so this also feeds its existing affinity/personalization scoring.
 */
function sendAddToCartEvent(productId) {
  try {
    if (!window.gdjConsent || window.gdjConsent.allowed() !== true) return;
    const headerEl = document.querySelector('[data-search-api-url]');
    const api = headerEl && (headerEl.dataset.searchApiUrl || '').replace(/\/$/, '');
    const shop = headerEl && headerEl.dataset.shop;
    if (!api || !shop) return;

    const SID_KEY = 'gdj_search_session';
    let sessionId;
    try {
      sessionId = localStorage.getItem(SID_KEY);
      if (!sessionId) {
        sessionId = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(SID_KEY, sessionId);
      }
    } catch (_) { return; }

    const body = JSON.stringify({
      shop, session_id: sessionId,
      events: [{ type: 'add_to_cart', product_id: productId }],
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${api}/api/v1/events`, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(`${api}/api/v1/events`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch (_) {}
}

/**
 * Pre-order plan for this variant (Timesact). The PDP adds pre-orders with their
 * selling plan; without it the line is a plain purchase (no « Précommandez »
 * note, no ship date) and sits next to the real pre-order line in the cart.
 */
function preorderPlanId(product, variant) {
  const groups = (product && product.selling_plan_groups) || [];
  const planIds = new Set();
  groups
    .filter((g) => g.app_id === 'timesact' || /pr[eé]-?commande|pre-?order/i.test(g.name || ''))
    .forEach((g) => (g.selling_plans || []).forEach((sp) => planIds.add(sp.id)));
  const allocation = ((variant && variant.selling_plan_allocations) || []).find((a) => planIds.has(a.selling_plan_id));
  return allocation ? allocation.selling_plan_id : null;
}

/** Add a variant through Horizon's cart event contract. */
async function addVariant(variant, quantity, properties, sellingPlanId) {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });

  document.body.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: variant.id, quantity }],
    promise,
  }));

  try {
    const item = { id: variant.id, quantity };
    if (properties) item.properties = properties;
    if (sellingPlanId) item.selling_plan = sellingPlanId;
    const addRes = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [item],
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    }).then((r) => r.json());

    if (addRes.status) throw new Error(addRes.description || 'Add to cart failed');

    const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections: addRes.sections, source: 'gdj-quick-add', didError: false },
    });
    applyCart(cart); // reflect the new quantities on the cards immediately
  } catch (error) {
    reject(error);
    throw error;
  }
}

/** Promise<number|null> — quantity to add, or null if cancelled. */
function openQtyPopup(inCart) {
  return new Promise((resolve) => {
    const pop = document.querySelector('[data-gdj-qa-pop]');
    if (!pop) { resolve(1); return; }
    const input = pop.querySelector('[data-gdj-qa-input]');
    const titleEl = pop.querySelector('[data-gdj-qa-title]');
    const incartStr = pop.dataset.strIncart || 'Already in your cart: {count}.';
    const howMany = pop.dataset.strHowmany || 'How many more would you like to add?';

    titleEl.textContent = `${incartStr.replace('{count}', inCart)} ${howMany}`;
    input.value = '1';
    pop.hidden = false;
    requestAnimationFrame(() => pop.classList.add('is-open'));
    input.focus();
    input.select();

    const clamp = () => Math.max(1, parseInt(input.value, 10) || 1);
    function cleanup(val) {
      pop.classList.remove('is-open');
      setTimeout(() => { pop.hidden = true; }, 200);
      pop.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
      resolve(val);
    }
    function onClick(e) {
      if (e.target.closest('[data-gdj-qa-cancel]')) cleanup(null);
      else if (e.target.closest('[data-gdj-qa-confirm]')) cleanup(clamp());
      else if (e.target.closest('[data-gdj-qa-inc]')) input.value = String(clamp() + 1);
      else if (e.target.closest('[data-gdj-qa-dec]')) input.value = String(Math.max(1, clamp() - 1));
    }
    function onKey(e) {
      if (e.key === 'Escape') cleanup(null);
      else if (e.key === 'Enter') { e.preventDefault(); cleanup(clamp()); }
    }
    pop.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
  });
}

async function handleQuickAdd(handle, productId, btn) {
  const product = await getProduct(handle);
  const variants = product.variants || [];
  // Let the customer pick options when there's more than one variant.
  if (variants.length > 1) { window.location.href = `${root()}products/${handle}`; return; }
  const variant = variants.find((v) => v.available) || variants[0];
  if (!variant) { window.location.href = `${root()}products/${handle}`; return; }

  const inCart = cartQty(productId || product.id, handle);
  let quantity = 1;
  if (inCart > 0) {
    quantity = await openQtyPopup(inCart);
    if (!quantity) return; // cancelled
  }

  // Fulfillment guard: a cart can't mix in-store-pickup-only products with
  // deliverable ones. data-pickup-only comes from the card (Liquid or the
  // engine-rendered search card).
  const addingPickup = !!(btn && btn.dataset && btn.dataset.pickupOnly === 'true');
  if (window.gdjFulfillment) {
    const conflict = window.gdjFulfillment.conflictFor(addingPickup);
    if (conflict) {
      const ok = await window.gdjFulfillment.confirm(conflict);
      if (!ok) return; // cancelled
      await window.gdjFulfillment.clearConflicting(conflict);
    }
  }

  // Assortiment products: ask which model/color the customer wants and attach
  // it to the cart line (same "Préférence" property as the PDP flow).
  let properties = null;
  if ((product.tags || []).includes('Assortiment') && window.gdjAssortPrompt) {
    const note = await window.gdjAssortPrompt();
    if (note === null) return; // cancelled
    if (note) properties = { [window.gdjAssortPropName || 'Préférence']: note };
  }

  // Pickup-only items carry the same visible line-item property as the PDP flow
  // so the restriction rides into the cart + checkout.
  if (addingPickup) {
    properties = properties || {};
    properties[(window.gdjFulfillment && window.gdjFulfillment.PROP) || 'SHIPPING NOT AVAILABLE'] = 'PICKUP ONLY';
  }

  await addVariant(variant, quantity, properties, preorderPlanId(product, variant));
  sendAddToCartEvent(productId || product.id);
}

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-gdj-quick-add]');
  if (!btn) return;
  event.preventDefault();
  if (btn.dataset.loading) return;
  btn.dataset.loading = '1';
  btn.classList.add('is-loading');
  const card = btn.closest('.gdj-card');
  const handle = btn.getAttribute('data-gdj-quick-add');
  handleQuickAdd(handle, card && card.dataset.productId, btn)
    .catch(() => { window.location.href = `${root()}products/${handle}`; })
    .finally(() => { delete btn.dataset.loading; btn.classList.remove('is-loading'); });
});

// Keep cart state + badges current.
refreshCartState();
document.addEventListener('shopify:cart:lines-update', () => setTimeout(refreshCartState, 200));
window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCartState(); });

// Re-apply badges when cards are rendered dynamically (Algolia / favourites).
let badgeTimer;
const main = document.querySelector('#MainContent') || document.body;
new MutationObserver(() => {
  clearTimeout(badgeTimer);
  badgeTimer = setTimeout(updateBadges, 150);
}).observe(main, { childList: true, subtree: true });
